


import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  Autocomplete,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { API_BASE_URL } from '../../../config';
import api from '../../../api/axiosInterceptor';

// Format JS Date to DD-MM-YYYY
const dateToDisplayString = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB').replace(/\//g, '-');
};

// Convert DD-MM-YYYY or DD/MM/YYYY to ISO YYYY-MM-DD for date inputs
const convertDisplayDateToISO = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.includes('/') ? dateStr.split('/') : dateStr.split('-');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export const AdminAssetAuctionComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(true);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [formData, setFormData] = useState({
    asset_property_base: null,
    khata_holder: '',
    date_of_auction: '',
    final_bid_amt: '',
    success_bid_amt: '',
    gst: '',
    tcs: '',
    total: '',
    success_bidder_name: '',
    auction_amt: '',
    realized_amt: '',
  });

  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl. No', width: 70 },
    { id: 'name_of_fe', field: 'name_of_fe', headerName: 'Financial Establishment', width: 250 },
    { id: 'name_of_ca', field: 'name_of_ca', headerName: 'Competent Authority', width: 200 },
    { id: 'notification', field: 'notification', headerName: 'Notification', width: 150 },
    { id: 'date', field: 'date', headerName: 'Date', width: 120 },
    { id: 'notification_no', field: 'notification_no', headerName: 'Notification No', width: 150 },
    { id: 'property_detals', field: 'property_detals', headerName: 'Property Details', width: 200 },
    { id: 'survey_no', field: 'survey_no', headerName: 'Survey No', width: 120 },
    { id: 'property_type', field: 'property_type', headerName: 'Property Type', width: 150 },
    { id: 'khata_holder', field: 'khata_holder', headerName: 'Khata Holder', width: 200 },
    { id: 'date_of_auction', field: 'date_of_auction', headerName: 'Date of Auction', width: 140 },
    { id: 'final_bid_amt', field: 'final_bid_amt', headerName: 'Final Bid Amount', width: 150 },
    { id: 'success_bid_amt', field: 'success_bid_amt', headerName: 'Success Bid Amount', width: 150 },
    { id: 'gst', field: 'gst', headerName: 'GST', width: 100 },
    { id: 'tcs', field: 'tcs', headerName: 'TCS', width: 100 },
    { id: 'total', field: 'total', headerName: 'Total', width: 120 },
    { id: 'success_bidder_name', field: 'success_bidder_name', headerName: 'Success Bidder Name', width: 200 },
    { id: 'auction_amt', field: 'auction_amt', headerName: 'Auction Amount', width: 150 },
    { id: 'realized_amt', field: 'realized_amt', headerName: 'Realized Amount', width: 150 },

    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}><Edit /></IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)}><Delete /></IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchPropertyOptions();
    fetchData();
  }, []);

  const fetchPropertyOptions = () => {
    setPropertyLoading(true);
    axios.get(`${API_BASE_URL}/asset-mgmt/asset-property/`)
      .then(res => setPropertyOptions(res.data))
      .catch(() => {
        setSnackbarMessage('Failed to load Asset Property options.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => setPropertyLoading(false));
  };

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/asset-mgmt/asset-auction/`)
      .then(response => {
        const apiData = response.data.map((item, index) => ({
          id: item.id,
          slNo: index + 1,
          name_of_fe: item.asset_property_base?.asset3_2_base?.fe?.name_of_fe || '-',
          name_of_ca: item.asset_property_base?.asset3_2_base?.fe?.name_of_ca || '-',
          notification: item.asset_property_base?.asset3_2_base?.notification || '-',
          date: item.asset_property_base?.asset3_2_base?.date ? dateToDisplayString(item.asset_property_base.asset3_2_base.date) : '-',
          notification_no: item.asset_property_base?.asset3_2_base?.notification_no || '-',
          property_detals: item.asset_property_base?.property_detals || '-',
          survey_no: item.asset_property_base?.survey_no || '-',
          property_type: item.asset_property_base?.property_type || '-',
          khata_holder: item.khata_holder || '-',
          date_of_auction: item.date_of_auction ? dateToDisplayString(item.date_of_auction) : '-',
          final_bid_amt: item.final_bid_amt || '-',
          success_bid_amt: item.success_bid_amt || '-',
          gst: item.gst || '-',
          tcs: item.tcs || '-',
          total: item.total || '-',
          success_bidder_name: item.success_bidder_name || '-',
          auction_amt: item.auction_amt || '-',
          realized_amt: item.realized_amt || '-',
          asset_property_baseId: item.asset_property_base?.id || null,
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch(() => {
        setSnackbarMessage('Failed to load Asset Auction data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedRecord(null);
    setFormData({
      asset_property_base: null,
      khata_holder: '',
      date_of_auction: '',
      final_bid_amt: '',
      success_bid_amt: '',
      gst: '',
      tcs: '',
      total: '',
      success_bidder_name: '',
      auction_amt: '',
      realized_amt: '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (record) => {
    const selectedProp = propertyOptions.find(opt => opt.id === record.asset_property_baseId) || null;
    setIsEditMode(true);
    setSelectedRecord(record);
    setFormData({
      asset_property_base: selectedProp,
      khata_holder: record.khata_holder || '',
      date_of_auction: record.date_of_auction ? convertDisplayDateToISO(dateToDisplayString(record.date_of_auction)) : '',
      final_bid_amt: record.final_bid_amt === '-' ? '' : record.final_bid_amt,
      success_bid_amt: record.success_bid_amt === '-' ? '' : record.success_bid_amt,
      gst: record.gst === '-' ? '' : record.gst,
      tcs: record.tcs === '-' ? '' : record.tcs,
      total: record.total === '-' ? '' : record.total,
      success_bidder_name: record.success_bidder_name === '-' ? '' : record.success_bidder_name,
      auction_amt: record.auction_amt === '-' ? '' : record.auction_amt,
      realized_amt: record.realized_amt === '-' ? '' : record.realized_amt,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (record) => {
    setDeleteRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/asset-mgmt/asset-auction/${deleteRecord.id}/`)
      .then(() => {
        setSnackbarMessage('Record deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchData();
      })
      .catch(() => {
        setSnackbarMessage('Failed to delete record.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteRecord(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.asset_property_base) errors.asset_property_base = 'Property Details selection is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = () => {
    if (!validateForm()) return;
    setSubmitLoading(true);
    const payload = {
      asset_property_base: formData.asset_property_base.id,
      khata_holder: formData.khata_holder,
      date_of_auction: formData.date_of_auction || null,
      final_bid_amt: formData.final_bid_amt,
      success_bid_amt: formData.success_bid_amt,
      gst: formData.gst,
      tcs: formData.tcs,
      total: formData.total,
      success_bidder_name: formData.success_bidder_name,
      auction_amt: formData.auction_amt,
      realized_amt: formData.realized_amt,
    };

    const request = isEditMode
      ? api.patch(`/asset-mgmt/asset-auction/${selectedRecord.id}/`, payload)
      : api.post(`/asset-mgmt/asset-auction/`, payload);

    request.then(() => {
      setSnackbarMessage(isEditMode ? 'Record updated successfully.' : 'Record added successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchData();
    }).catch(() => {
      setSnackbarMessage(isEditMode ? 'Failed to update record.' : 'Failed to add record.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }).finally(() => {
      setSubmitLoading(false);
      setOpenFormDialog(false);
    });
  };

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">Admin Asset Auction Master</Typography>
        <Button variant="contained" onClick={handleAdd}>Add Record</Button>
      </Box>

      {loading ? <LoadingSpinner size={30} /> : (
        <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{
          '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' },
        }} />
      )}

      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{isEditMode ? 'Edit Record' : 'Add Record'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Autocomplete
            options={propertyOptions}
            getOptionLabel={(option) => {
              const feName = option.asset3_2_base?.fe?.name_of_fe || '';
              const propDetals = option.property_detals || '';
              return `${feName} - ${propDetals}`;
            }}
            loading={propertyLoading}
            value={formData.asset_property_base}
            onChange={(e, newValue) => setFormData({ ...formData, asset_property_base: newValue })}
            filterOptions={(options, { inputValue }) =>
              options.filter(option =>
                (option.asset3_2_base?.fe?.name_of_fe?.toLowerCase().includes(inputValue.toLowerCase()) ||
                 option.property_detals?.toLowerCase().includes(inputValue.toLowerCase()))
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Financial Establishment + Property Details"
                margin="dense"
                error={!!formErrors.asset_property_base}
                helperText={formErrors.asset_property_base}
                required
              />
            )}
          />
          <TextField label="Khata Holder" value={formData.khata_holder} onChange={e => setFormData({ ...formData, khata_holder: e.target.value })} fullWidth margin="dense" />
          <TextField label="Date of Auction" type="date" value={formData.date_of_auction} fullWidth margin="dense" InputLabelProps={{ shrink: true }}
            onChange={e => setFormData({ ...formData, date_of_auction: e.target.value })}
          />
          <TextField label="Final Bid Amount" value={formData.final_bid_amt} onChange={e => setFormData({ ...formData, final_bid_amt: e.target.value })} fullWidth margin="dense" />
          <TextField label="Success Bid Amount" value={formData.success_bid_amt} onChange={e => setFormData({ ...formData, success_bid_amt: e.target.value })} fullWidth margin="dense" />
          <TextField label="GST" value={formData.gst} onChange={e => setFormData({ ...formData, gst: e.target.value })} fullWidth margin="dense" />
          <TextField label="TCS" value={formData.tcs} onChange={e => setFormData({ ...formData, tcs: e.target.value })} fullWidth margin="dense" />
          <TextField label="Total" value={formData.total} onChange={e => setFormData({ ...formData, total: e.target.value })} fullWidth margin="dense" />
          <TextField label="Success Bidder Name" value={formData.success_bidder_name} onChange={e => setFormData({ ...formData, success_bidder_name: e.target.value })} fullWidth margin="dense" />
          <TextField label="Auction Amount" value={formData.auction_amt} onChange={e => setFormData({ ...formData, auction_amt: e.target.value })} fullWidth margin="dense" />
          <TextField label="Realized Amount" value={formData.realized_amt} onChange={e => setFormData({ ...formData, realized_amt: e.target.value })} fullWidth margin="dense" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)} disabled={submitLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSubmit} disabled={submitLoading}>
            {submitLoading ? <LoadingSpinner size={20} /> : (isEditMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={submitLoading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
